import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

function ShippingManagementSection({ shippingOptions, onUpdateShipping }) {
  const [shipping, setShipping] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingShipping, setEditingShipping] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [newShipping, setNewShipping] = useState({ method: '', name: '', cost: '', days: '', min_quantity: '', min_amount: '', requires_address: true });

  useEffect(() => {
    fetchShipping();
  }, []);

  const fetchShipping = async () => {
    try {
      const response = await axios.get('/admin/shipping');
      setShipping(response.data.shipping_options || []);
    } catch (error) {
      console.error('Failed to fetch shipping:', error);
    }
  };

  const startEditingShipping = (shippingOption) => {
    setEditingShipping(shippingOption.id);
    setEditValues({
      method: shippingOption.method,
      name: shippingOption.name,
      cost: (shippingOption.price / 100).toFixed(2),
      days: shippingOption.delivery_days,
      min_quantity: shippingOption.min_quantity || '',
      min_amount: shippingOption.min_amount ? (shippingOption.min_amount / 100).toFixed(2) : '',
      requires_address: shippingOption.requires_address !== false
    });
  };

  const handleUpdateShipping = async (shippingId) => {
    setLoading(true);
    try {
      await axios.put(`/admin/shipping/${shippingId}`, {
        method: editValues.method,
        name: editValues.name,
        price: Math.round(parseFloat(editValues.cost) * 100),
        delivery_days: editValues.days,
        min_quantity: editValues.min_quantity ? parseInt(editValues.min_quantity) : null,
        min_amount: editValues.min_amount ? Math.round(parseFloat(editValues.min_amount) * 100) : null,
        requires_address: editValues.requires_address !== false
      });
      toast.success('✅ Shipping option updated successfully');
      setEditingShipping(null);
      setEditValues({});
      fetchShipping();
      if (onUpdateShipping) onUpdateShipping();
    } catch (error) {
      console.error('Failed to update shipping:', error);
      toast.error('Failed to update shipping');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShipping = async (shippingId) => {
    if (!window.confirm('Are you sure you want to delete this shipping option?')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`/admin/shipping/${shippingId}`);
      toast.success('✅ Shipping option deleted successfully');
      fetchShipping();
      if (onUpdateShipping) onUpdateShipping();
    } catch (error) {
      console.error('Failed to delete shipping:', error);
      toast.error('Failed to delete shipping');
    } finally {
      setLoading(false);
    }
  };

  const handleAddShipping = async () => {
    if (!newShipping.method || !newShipping.name || !newShipping.cost) {
      toast.error('Please fill in method, name, and cost');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/admin/shipping', {
        method: newShipping.method,
        name: newShipping.name,
        price: Math.round(parseFloat(newShipping.cost) * 100),
        delivery_days: newShipping.days || '3-5 business days',
        min_quantity: newShipping.min_quantity ? parseInt(newShipping.min_quantity) : null,
        min_amount: newShipping.min_amount ? Math.round(parseFloat(newShipping.min_amount) * 100) : null,
        requires_address: newShipping.requires_address !== false,
        active: true
      });
      toast.success('✅ Shipping option added successfully');
      setNewShipping({ method: '', name: '', cost: '', days: '', min_quantity: '', min_amount: '', requires_address: true });
      fetchShipping();
      if (onUpdateShipping) onUpdateShipping();
    } catch (error) {
      console.error('Failed to add shipping:', error);
      const message = error.response?.data?.detail || 'Failed to add shipping';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Shipping Management</h2>
        <p className="text-gray-600">Manage shipping options, pricing, and delivery times</p>
      </div>

      {/* Add New Shipping */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Shipping Option</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Method ID (e.g., express)"
            value={newShipping.method}
            onChange={(e) => setNewShipping(prev => ({ ...prev, method: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder='Display Name (e.g., Express Shipping)'
            value={newShipping.name}
            onChange={(e) => setNewShipping(prev => ({ ...prev, name: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Cost (e.g., 15.00)"
            value={newShipping.cost}
            onChange={(e) => setNewShipping(prev => ({ ...prev, cost: e.target.value.replace(/[^0-9.]/g, '') }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Delivery (e.g., 1-2 days)"
            value={newShipping.days}
            onChange={(e) => setNewShipping(prev => ({ ...prev, days: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="number"
            placeholder="Min Quantity (optional)"
            value={newShipping.min_quantity}
            onChange={(e) => setNewShipping(prev => ({ ...prev, min_quantity: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            min="0"
          />
          <input
            type="text"
            placeholder="Min Amount $ (optional)"
            value={newShipping.min_amount}
            onChange={(e) => setNewShipping(prev => ({ ...prev, min_amount: e.target.value.replace(/[^0-9.]/g, '') }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mt-4 flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={newShipping.requires_address}
              onChange={(e) => setNewShipping(prev => ({ ...prev, requires_address: e.target.checked }))}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Requires Delivery Address</span>
          </label>
          <p className="text-xs text-gray-500">Uncheck for pickup or non-delivery options</p>
        </div>
        <div className="mt-4">
          <Button 
            onClick={handleAddShipping}
            disabled={loading}
            className="bg-custom-orange hover:bg-custom-orange"
          >
            {loading ? 'Adding...' : 'Add Shipping Option'}
          </Button>
        </div>
      </Card>

      {/* Current Shipping Options */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Shipping Options</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2">Method</th>
                <th className="text-left py-3 px-2">Display Name</th>
                <th className="text-left py-3 px-2">Cost</th>
                <th className="text-left py-3 px-2">Delivery Time</th>
                <th className="text-left py-3 px-2">Min Qty</th>
                <th className="text-left py-3 px-2">Min Amount</th>
                <th className="text-left py-3 px-2">Requires Address</th>
                <th className="text-left py-3 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shipping.map((option) => (
                <tr key={option.id} className="border-b border-gray-100 hover:bg-gray-50">
                  {editingShipping === option.id ? (
                    // Edit mode
                    <>
                      <td className="py-3 px-2">
                        <input
                          type="text"
                          value={editValues.method}
                          onChange={(e) => setEditValues(prev => ({ ...prev, method: e.target.value }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="text"
                          value={editValues.name}
                          onChange={(e) => setEditValues(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-600">$</span>
                          <input
                            type="text"
                            value={editValues.cost}
                            onChange={(e) => setEditValues(prev => ({ ...prev, cost: e.target.value.replace(/[^0-9.]/g, '') }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="text"
                          value={editValues.days}
                          onChange={(e) => setEditValues(prev => ({ ...prev, days: e.target.value }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="number"
                          value={editValues.min_quantity}
                          onChange={(e) => setEditValues(prev => ({ ...prev, min_quantity: e.target.value }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="—"
                          min="0"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="text"
                          value={editValues.min_amount}
                          onChange={(e) => setEditValues(prev => ({ ...prev, min_amount: e.target.value.replace(/[^0-9.]/g, '') }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="—"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <input
                          type="checkbox"
                          checked={editValues.requires_address !== false}
                          onChange={(e) => setEditValues(prev => ({ ...prev, requires_address: e.target.checked }))}
                          className="w-4 h-4"
                        />
                      </td>
                      <td className="py-3 px-2 space-x-1">
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateShipping(option.id)}
                          disabled={loading}
                          className="bg-custom-orange hover:bg-custom-orange text-white"
                        >
                          Save
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingShipping(null);
                            setEditValues({});
                          }}
                        >
                          Cancel
                        </Button>
                      </td>
                    </>
                  ) : (
                    // View mode
                    <>
                      <td className="py-3 px-2 font-medium">{option.method}</td>
                      <td className="py-3 px-2">{option.name}</td>
                      <td className="py-3 px-2">${(option.price / 100).toFixed(2)}</td>
                      <td className="py-3 px-2">{option.delivery_days}</td>
                      <td className="py-3 px-2 text-gray-600">{option.min_quantity || '—'}</td>
                      <td className="py-3 px-2 text-gray-600">
                        {option.min_amount ? `$${(option.min_amount / 100).toFixed(2)}` : '—'}
                      </td>
                      <td className="py-3 px-2">
                        <Badge variant={option.requires_address !== false ? 'default' : 'outline'}>
                          {option.requires_address !== false ? '✓ Yes' : '✗ No'}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => startEditingShipping(option)}
                          disabled={loading}
                          data-testid={`edit-shipping-${option.id}`}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDeleteShipping(option.id)}
                          disabled={loading}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


export default ShippingManagementSection;
